import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Box,
  Button,
  Input,
  Menu,
  MenuButton,
  MenuList,
  Text,
  HStack,
  Tooltip,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  MenuItem
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { faMagnifyingGlass, faPlus, faX } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { ButtonText, FormFeedback } from '@morressier/alembic';
import {
  IAutosuggestItem,
  IAutosuggestSearchMethod
} from 'src/api/autosuggest';

export type AutosuggestValue =
  | ({ name?: string; termId?: string } & Record<string, unknown>)
  | string
  | null
  | undefined;

type AutosuggestSelectProps = {
  value: AutosuggestValue;
  onChange: (value: unknown) => void;
  searchMethod: IAutosuggestSearchMethod;
  queryKey: string;
  termSearch?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  placeholder: string;
  errorMessage?: string;
  isOptionDisabled?: (item: IAutosuggestItem) => boolean;
  limit?: number;
  level?: number;
  allowNewKeyword?: boolean;
  onNewKeywordClick?: () => void;
  newKeywordLabel?: string;
};

const DEFAULT_LIMIT = 50;

const toTitleCase = (sentence: string) =>
  sentence
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, match => match.toUpperCase());

export const AutosuggestSelect: React.FC<AutosuggestSelectProps> = ({
  value,
  onChange,
  searchMethod,
  queryKey,
  termSearch,
  isDisabled = false,
  isInvalid = false,
  placeholder,
  errorMessage,
  isOptionDisabled,
  limit = DEFAULT_LIMIT,
  level,
  allowNewKeyword = true,
  onNewKeywordClick,
  newKeywordLabel
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);

  // Root mode searches by typed text; dependent mode fetches by the parent
  // termId and filters client-side using the current search text.
  const isDependent = !!termSearch;
  const searchQuery = isDependent ? termSearch : search || ' ';

  const { data } = useQuery({
    queryKey: [queryKey, searchQuery, level],
    queryFn: () => searchMethod(searchQuery, limit, level),
    enabled: !!searchQuery
  });

  const listData = data?.data ?? [];

  const filteredListData = useMemo(() => {
    if (listData.length === 0) return listData;

    const queryLower = search.trim().toLowerCase();
    if (!queryLower) return listData;

    return listData.filter(item =>
      item.name?.toLowerCase().includes(queryLower)
    );
  }, [listData, search]);

  useEffect(() => {
    if (buttonRef.current) {
      const { width } = buttonRef.current.getBoundingClientRect();
      setMenuWidth(width);
    }
  }, []);

  const handleItemSelect = useCallback(
    (item: IAutosuggestItem) => {
      onChange(item);
      setMenuOpen(false);
      setSearch('');
    },
    [onChange]
  );

  const handleClose = () => {
    setMenuOpen(!menuOpen);
    setSearch('');
  };

  const selectedLabel =
    typeof value === 'object' && value ? value.name : undefined;
  const displayLabel = selectedLabel?.trim()
    ? selectedLabel
    : `Select a ${placeholder}`;

  const menuButton = (
    <MenuButton
      as={Button}
      variant="unstyled"
      rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
      onClick={handleClose}
      width="100%"
      minWidth="0"
      textAlign="left"
      backgroundColor={isInvalid ? 'errorPrimaryFill' : 'inputFill'}
      border="1px solid"
      borderWidth="1px"
      borderStyle="solid"
      boxShadow="none"
      borderRadius="small"
      color="inputContent"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      _focus={{
        boxShadow: 'none',
        bg: isInvalid ? 'errorPrimaryFill' : 'inputFill',
        borderColor: isInvalid ? 'errorPrimaryBorder' : 'inputBorder'
      }}
      _focusVisible={{
        layerStyle: 'focus',
        boxShadow: 'none',
        bg: isInvalid ? 'errorPrimaryFill' : 'inputFill',
        borderColor: isInvalid ? 'errorPrimaryBorder' : 'inputBorder'
      }}
      _hover={{
        bg: isInvalid ? 'errorPrimaryFill' : 'inputFill',
        borderColor: isInvalid ? 'errorPrimaryBorder' : 'inputBorderHover'
      }}
      _active={{
        bg: isInvalid ? 'errorPrimaryFill' : 'inputFill',
        borderColor: isInvalid ? 'errorPrimaryBorder' : 'inputBorderActive'
      }}
      _expanded={{
        bg: isInvalid ? 'errorPrimaryFill' : 'inputFill',
        borderColor: isInvalid ? 'errorPrimaryBorder' : 'inputBorderActive',
        boxShadow: 'none'
      }}
      cursor="pointer"
      ref={buttonRef}
      height="40px"
      py="8px"
      px={2}
      borderColor={isInvalid ? 'errorPrimaryBorder' : 'inputBorder'}
      disabled={isDisabled}
    >
      <HStack>
        <Text
          textStyle="textBody"
          isTruncated
          color={selectedLabel ? (isInvalid ? 'error' : 'inherit') : 'GrayText'}
        >
          {displayLabel}
        </Text>
      </HStack>
    </MenuButton>
  );

  return (
    <>
      <Menu isOpen={menuOpen} onClose={() => setMenuOpen(false)}>
        {selectedLabel ? (
          <Tooltip label={selectedLabel} placement="top" hasArrow>
            {menuButton}
          </Tooltip>
        ) : (
          menuButton
        )}
        <MenuList
          width="auto"
          minWidth={menuWidth ? `${menuWidth}px` : 'auto'}
          maxH="250px"
          overflowY="auto"
          overscrollBehavior="contain"
          zIndex="1007"
          p={0}
        >
          <Box
            position="sticky"
            top="0"
            bg="surfacePrimaryFill"
            zIndex="1"
            p={2}
          >
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </InputLeftElement>
              <Input
                width="100%"
                minWidth="0"
                flex="1"
                border="1px solid"
                aria-label={`input-search-for-${placeholder}`}
                borderColor="neutralPrimaryBorder"
                placeholder={`Search for ${placeholder}`}
                value={search}
                onChange={e => setSearch(e.target.value)}
                pl="2.2rem"
                autoFocus
              />
              {search && (
                <InputRightElement
                  onClick={() => setSearch('')}
                  cursor={'pointer'}
                >
                  <FontAwesomeIcon icon={faX} size="sm" />
                </InputRightElement>
              )}
            </InputGroup>
          </Box>

          {filteredListData?.length > 0 ? (
            filteredListData?.map((item: IAutosuggestItem) => {
              const optionDisabled = isOptionDisabled?.(item) ?? false;

              return (
                <MenuItem
                  key={item.termId}
                  px={2}
                  py={1}
                  borderRadius="md"
                  _hover={
                    optionDisabled
                      ? undefined
                      : { bg: 'actionSecondaryFill', cursor: 'pointer' }
                  }
                  onClick={() => !optionDisabled && handleItemSelect(item)}
                  isDisabled={optionDisabled}
                >
                  <HStack py={1}>
                    <Text textStyle="textBody">{item.name}</Text>
                  </HStack>
                </MenuItem>
              );
            })
          ) : (
            <Box p={2} textAlign={'center'}>
              <Text color="GrayText">No data found</Text>
            </Box>
          )}

          {allowNewKeyword && onNewKeywordClick && (
            <Box
              position="sticky"
              bottom="0"
              bg="surfacePrimaryFill"
              py={2}
              borderTopWidth="1px"
              borderTopColor="gray.200"
              zIndex="1"
            >
              <MenuItem
                key="new-keyword"
                px={2}
                borderRadius="md"
                _hover={{
                  bg: 'actionSecondaryFill',
                  cursor: 'pointer'
                }}
                onClick={onNewKeywordClick}
              >
                <ButtonText iconLeft={<FontAwesomeIcon icon={faPlus} />}>
                  New {toTitleCase(newKeywordLabel ?? placeholder)}
                </ButtonText>
              </MenuItem>
            </Box>
          )}
        </MenuList>
      </Menu>
      {errorMessage && (
        <FormFeedback variant="error">{errorMessage}</FormFeedback>
      )}
    </>
  );
};

export default AutosuggestSelect;
